import { useState, useEffect } from "react";
import { FaSave, FaBullhorn } from "react-icons/fa";
import { toast } from "sonner";

import adminService from "../../../api/services/admin";
import settingsService from "../../../api/services/settings";
import {
  SectionHeader,
  SectionTitle,
  Button,
  Container,
  Section,
  SectionSubTitle,
  SectionHint,
  ToggleRow,
  ToggleLabel,
  ToggleSwitch,
  TextInput,
  CharCount,
  PreviewBar,
  Actions,
  LoadingContainer,
} from "./AdminSettings.styles";

const MAX_TEXT_LENGTH = 300;

const AdminSettings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [text, setText] = useState("");

  // Učitaj trenutna podešavanja
  useEffect(() => {
    let mounted = true;

    settingsService
      .getPromoTicker()
      .then((response) => {
        if (mounted && response?.promoTicker) {
          setEnabled(Boolean(response.promoTicker.enabled));
          setText(response.promoTicker.text || "");
        }
      })
      .catch(() => {
        toast.error("Greška pri učitavanju podešavanja");
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleSave = async () => {
    if (enabled && !text.trim()) {
      toast.error("Unesi tekst trake ili je isključi");
      return;
    }
    if (text.length > MAX_TEXT_LENGTH) {
      toast.error(`Tekst ne sme biti duži od ${MAX_TEXT_LENGTH} karaktera`);
      return;
    }

    setIsSaving(true);
    try {
      const result = await adminService.updatePromoTicker({
        enabled,
        text: text.trim(),
      });

      // Osveži keš da javne stranice odmah vide promenu, bez reload-a
      settingsService.setPromoTickerCache(result.promoTicker);

      toast.success("Podešavanja su sačuvana!");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Greška pri čuvanju podešavanja",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <SectionHeader>
        <SectionTitle>Podešavanja Sajta</SectionTitle>
      </SectionHeader>

      {isLoading ? (
        <LoadingContainer>
          <l-line-spinner
            size="40"
            stroke="3"
            speed="1"
            color="var(--accent)"
          />
        </LoadingContainer>
      ) : (
        <Container>
          <Section>
            <SectionSubTitle>
              <FaBullhorn />
              1. Promo traka — status
            </SectionSubTitle>
            <SectionHint>
              Pokretna traka na vrhu sajta za najave akcija i popusta. Promene
              su odmah vidljive svim posetiocima — nije potreban deploy.
            </SectionHint>

            <ToggleRow>
              <ToggleSwitch
                type="button"
                $on={enabled}
                onClick={() => setEnabled(!enabled)}
                aria-label={enabled ? "Isključi traku" : "Uključi traku"}
              />
              <ToggleLabel $active={enabled}>
                {enabled ? "Traka je uključena" : "Traka je isključena"}
              </ToggleLabel>
            </ToggleRow>
          </Section>

          <Section>
            <SectionSubTitle>2. Tekst trake</SectionSubTitle>
            <TextInput
              id="ticker-text"
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="npr. Povodom praznika 30% popusta na sve kurseve — samo ovog vikenda!"
              maxLength={MAX_TEXT_LENGTH + 50}
              aria-label="Tekst trake"
            />
            <CharCount $over={text.length > MAX_TEXT_LENGTH}>
              {text.length}/{MAX_TEXT_LENGTH}
            </CharCount>
          </Section>

          <Section>
            <SectionSubTitle>3. Pregled i čuvanje</SectionSubTitle>
            <PreviewBar $dimmed={!enabled || !text.trim()}>
              {text.trim() || "Tekst trake će se prikazati ovde..."}
            </PreviewBar>

            <Actions>
              <Button
                $primary
                onClick={handleSave}
                disabled={isSaving}
                $loading={isSaving}
              >
                <FaSave />
                {isSaving ? "Čuvam..." : "Sačuvaj"}
              </Button>
            </Actions>
          </Section>
        </Container>
      )}
    </>
  );
};

export default AdminSettings;
